import {
  JCodeDetails,
  MedicalPaOrder,
  RegimenDoseData,
  VisitDataChartDetails,
} from "../../../data-model/medicalPaOrdersModel";
import { MedicalPaOrdersAuthStatus } from "../../../enums/medicalPaOrdersAuthStatus";
import { getAllJCodes } from "./existingDocsToLs";

/**
 * Checks if multiple past regimens exist for a medical PA order.
 *
 * This function determines whether any J-codes with "Auth on File" status in the given medical PA order
 * have multiple past regimens. A past regimen is considered to exist if there is a data point in the
 * visit data charts that occurs more than 30 days after the J-code's start date.
 *
 * @param {MedicalPaOrder} singleMedicalPaOrder - The medical PA order to check for multiple past regimens
 * @returns {boolean} Returns `true` if any J-code with "Auth on File" status has a past regimen data point
 *                    that is more than 30 days after its start date; otherwise returns `false`
 *
 * @example
 * const order = { ... }; // MedicalPaOrder object
 * const hasMultiplePastRegimens = checkIfMultiplePastRegimenExists(order);
 * if (hasMultiplePastRegimens) {
 *   console.log('Order has multiple past regimens');
 * }
 */
export const checkIfMultiplePastRegimenExists = (
  singleMedicalPaOrder: MedicalPaOrder,
) => {
  // Get all J-codes from the order
  const jCodes = getAllJCodes(singleMedicalPaOrder);
  if (jCodes === undefined || !jCodes?.length) {
    return false;
  }

  // Filter for J-codes with "Auth on File" status
  const jCodesWithAuthOnFileStatus = getJCodesWithAuthOnFileStatus(
    jCodes,
  ).filter((jCode) => jCode?.num_visits !== 999);
  if (!jCodesWithAuthOnFileStatus?.length) {
    return false;
  }

  // Check if any J-code has multiple past regimens
  const visitDataCharts =
    singleMedicalPaOrder?.auth_on_file?.visit_data_charts ?? {};

  return jCodesWithAuthOnFileStatus.some((jCode) =>
    checkIfMultiplePastRegimenExistsForJCode(jCode, visitDataCharts),
  );
};

const getJCodesWithAuthOnFileStatus = (jCodes: JCodeDetails[]) => {
  return jCodes.filter(
    (jCode) =>
      jCode && jCode.auth_status === MedicalPaOrdersAuthStatus.AuthOnFile,
  );
};

const checkIfMultiplePastRegimenExistsForJCode = (
  jCodesWithAuthOnFileStatus: JCodeDetails,
  visitDataChartDetails: VisitDataChartDetails,
) => {
  const visitDataChartDetail =
    visitDataChartDetails?.[jCodesWithAuthOnFileStatus?.description ?? ""];
  if (
    visitDataChartDetail &&
    visitDataChartDetail?.total_rows &&
    visitDataChartDetail?.total_rows > 0
  ) {
    const allDataPoints = visitDataChartDetail.data ?? [];
    const mostRecentNextDataPoint =
      getTheMostRecentNextDataPointFromJCodeFromDate(
        allDataPoints,
        jCodesWithAuthOnFileStatus?.start_date ?? "",
      );
    if (
      mostRecentNextDataPoint &&
      checkIfTheNextDataIsAfter30Days(
        mostRecentNextDataPoint,
        jCodesWithAuthOnFileStatus?.start_date ?? "",
      )
    ) {
      return true;
    }
  }
  return false;
};

const parseMMDDYYYY = (dateString: string): Date => {
  const [month, day, year] = dateString.split("/").map(Number);
  return new Date(year, month - 1, day);
};

const getTheMostRecentNextDataPointFromJCodeFromDate = (
  allDataPoints: RegimenDoseData[],
  fromDate: string,
): RegimenDoseData | undefined => {
  const fromDateTime = parseMMDDYYYY(fromDate).getTime();
  let closestFutureDataPoint: RegimenDoseData | undefined = undefined;
  let minDiff = Infinity;

  for (const dataPoint of allDataPoints) {
    const dataPointTime = parseMMDDYYYY(dataPoint.Date).getTime();
    if (dataPointTime > fromDateTime) {
      const diff = dataPointTime - fromDateTime;
      if (diff < minDiff) {
        minDiff = diff;
        closestFutureDataPoint = dataPoint;
      }
    }
  }

  return closestFutureDataPoint;
};

const checkIfTheNextDataIsAfter30Days = (
  dataPoint: RegimenDoseData,
  fromDate: string,
) => {
  const dataPointTime = parseMMDDYYYY(dataPoint.Date).getTime();
  const fromDateTime = parseMMDDYYYY(fromDate).getTime();
  const thirtyDaysInMs = 30 * 24 * 60 * 60 * 1000;
  return dataPointTime > fromDateTime + thirtyDaysInMs;
};
